import React from 'react';
import { Progress, Badge, Tooltip } from 'antd';
import {
  GlobalOutlined, WarningFilled, CheckCircleFilled,
  SendOutlined, ExclamationCircleFilled,
} from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const DomainHealthPanel = ({ domains = [] }) => {
  if (domains.length === 0) {
    return (
      <div style={{ ...glassCard, padding: 20, textAlign: 'center' }}>
        <GlobalOutlined style={{ fontSize: 28, color: COLORS.textMuted, marginBottom: 8 }} />
        <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>
          No domain health data available
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <GlobalOutlined style={{ color: COLORS.violet, fontSize: 16 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Domain Health</span>
        <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 4 }}>
          {domains.length} domain{domains.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Horizontal strip of domain cards */}
      <div style={{
        display: 'flex',
        gap: 12,
        overflowX: 'auto',
        paddingBottom: 4,
      }}>
        {domains.map((domain, idx) => {
          const bounceRate = domain.bounceRate || 0;
          const complaintRate = domain.complaintRate || 0;
          const dailySend = domain.dailySendCount || 0;
          const bounceWarning = bounceRate > 5;
          const complaintWarning = complaintRate > 0.1;
          const hasAlerts = domain.hasUnacknowledgedAlerts || bounceWarning || complaintWarning;

          return (
            <div
              key={domain.domain || idx}
              style={{
                ...glassCard,
                padding: 16,
                minWidth: 220,
                flex: '0 0 auto',
                position: 'relative',
                borderTop: `2px solid ${hasAlerts ? COLORS.rose : COLORS.emerald}`,
              }}
            >
              {/* Alert Badge */}
              {hasAlerts && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                }}>
                  <Badge dot color={COLORS.rose}>
                    <ExclamationCircleFilled style={{ color: COLORS.rose, fontSize: 14 }} />
                  </Badge>
                </div>
              )}

              {/* Domain Name */}
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: COLORS.text,
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                {hasAlerts ? (
                  <WarningFilled style={{ color: COLORS.rose, fontSize: 12 }} />
                ) : (
                  <CheckCircleFilled style={{ color: COLORS.emerald, fontSize: 12 }} />
                )}
                {domain.domain || 'Unknown Domain'}
              </div>

              {/* Bounce Rate */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}>
                  <span style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 500 }}>
                    Bounce Rate
                  </span>
                  <Tooltip title={bounceWarning ? 'Above 5% threshold - action required' : 'Within acceptable range'}>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: bounceWarning ? COLORS.rose : COLORS.emerald,
                    }}>
                      {bounceRate.toFixed(2)}%
                    </span>
                  </Tooltip>
                </div>
                <Progress
                  percent={Math.min(bounceRate * 10, 100)}
                  showInfo={false}
                  strokeColor={bounceWarning ? COLORS.rose : COLORS.emerald}
                  trailColor="rgba(255,255,255,0.06)"
                  size="small"
                />
              </div>

              {/* Complaint Rate */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}>
                  <span style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 500 }}>
                    Complaint Rate
                  </span>
                  <Tooltip title={complaintWarning ? 'Above 0.1% threshold - critical!' : 'Within acceptable range'}>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: complaintWarning ? COLORS.rose : COLORS.emerald,
                    }}>
                      {complaintRate.toFixed(3)}%
                    </span>
                  </Tooltip>
                </div>
                <Progress
                  percent={Math.min(complaintRate * 100, 100)}
                  showInfo={false}
                  strokeColor={complaintWarning ? COLORS.rose : COLORS.emerald}
                  trailColor="rgba(255,255,255,0.06)"
                  size="small"
                />
              </div>

              {/* Daily Send Count */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 10px',
                borderRadius: 8,
                background: COLORS.blueBg,
              }}>
                <SendOutlined style={{ color: COLORS.blue, fontSize: 12 }} />
                <span style={{ fontSize: 11, color: COLORS.textSecondary }}>Daily Sends</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.blue, marginLeft: 'auto' }}>
                  {dailySend.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DomainHealthPanel;
